import { useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  alpha,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { RiArrowDownSFill, RiCloseLine } from 'react-icons/ri';
import { Field, Form, FormikContext, useFormik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { InlineBadge } from 'components/UI';
import { InlineSelect, AmountTextField } from 'components/forms';
import { withDialog } from 'wrappers';
import { SortOrder } from 'types';

import { SortButton } from '../SortButton';
import { useCommonFiltersStyles } from '../useCommonFiltersStyles';
import { ALL_CURRENCIES_VALUE } from '../constants';
import { useCurrencyOptions, useProductTypeOptions } from '../options';
import { CommonFilterModalProps } from '../types';
import { secondaryDealFiltersHelper } from './helpers';
import { primaryDealSortVariantsMap } from './constants';
import { SecondaryDealFilterValues, ESecondaryDealSortOption } from './types';

const sortOptions = [
  ESecondaryDealSortOption.PublicationDate,
  ESecondaryDealSortOption.Currency,
  ESecondaryDealSortOption.ProductType,
  ESecondaryDealSortOption.DefaultRate,
  ESecondaryDealSortOption.Duration,
  ESecondaryDealSortOption.NominalValue,
  // ESecondaryDealSortOption.IRR,
  ESecondaryDealSortOption.Price,
  ESecondaryDealSortOption.PriceDiscount,
];

const useStyles = makeStyles((theme) => ({
  closeButtonContainer: {
    position: 'absolute',
    top: '12px',
    right: '12px',
  },

  body: {
    padding: '24px 24px 0px',
  },

  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '4px',
  },

  sortContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  sortGroup: {
    display: 'flex',
    gap: '16px',
    padding: '0 16px',
    flexWrap: 'wrap',
  },

  footer: {
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: `1px solid ${alpha(theme.palette.text.secondary, 0.16)}`,
    boxShadow: `0px 2px 6px 0px ${alpha(theme.palette.common.black, 0.15)}`,
  },
}));

type Props = CommonFilterModalProps<SecondaryDealFilterValues>;

export const SecondaryDealFilterModal = withDialog((props: Props) => {
  const { initialValues, withSort, contextCurrency, currencyOptions, onClear, onApply, onClose } = props;

  const commonFiltersStyles = useCommonFiltersStyles();
  const classes = useStyles();
  const intl = useIntl();

  const productTypeOptions = useProductTypeOptions(intl);
  const currencyFinalOptions = useCurrencyOptions(currencyOptions);

  const handleSubmit = (values: SecondaryDealFilterValues) => {
    onApply?.(values);
    onClose?.();
  };

  const formik = useFormik<SecondaryDealFilterValues>({
    initialValues: initialValues ?? secondaryDealFiltersHelper.getEmptyValue(),
    onSubmit: handleSubmit,
  });

  const getSortLabel = useCallback(
    (type: ESecondaryDealSortOption) => {
      switch (type) {
        case ESecondaryDealSortOption.PublicationDate:
          return intl.formatMessage({ id: 'publication_date' });

        case ESecondaryDealSortOption.Currency:
          return intl.formatMessage({ id: 'currency' });

        case ESecondaryDealSortOption.Price:
          return intl.formatMessage({ id: 'deal_price' });

        case ESecondaryDealSortOption.PriceDiscount:
          return intl.formatMessage({ id: 'discount_premium' });

        case ESecondaryDealSortOption.NominalValue:
          return intl.formatMessage({ id: 'nominal_value' });

        case ESecondaryDealSortOption.IRR:
          return intl.formatMessage({ id: 'interest' });

        case ESecondaryDealSortOption.Duration:
          return intl.formatMessage({ id: 'terms_left' });

        case ESecondaryDealSortOption.ProductType:
          return intl.formatMessage({ id: 'type' });

        case ESecondaryDealSortOption.DefaultRate:
          return intl.formatMessage({ id: 'expected_default_rate.initial.short' });

        default:
          return '';
      }
    },
    [intl],
  );

  const handleClear = () => {
    const clearValues = contextCurrency
      ? secondaryDealFiltersHelper.getValueFromPayload({ currency: [contextCurrency] })
      : secondaryDealFiltersHelper.getEmptyValue();

    formik.resetForm({ values: clearValues });
    onClear?.();
  };

  const currencyCount = (() => {
    const currencyValues = formik.values.currency as string[] | undefined;

    if (!currencyValues?.length) {
      return 0;
    }

    if (
      currencyValues.length === 1
      && (currencyValues[0] === ALL_CURRENCIES_VALUE || currencyValues[0] === contextCurrency)
    ) {
      return 0;
    }

    return currencyValues.length;
  })();

  const totalFiltersCount =
    secondaryDealFiltersHelper.countValues(formik.values)
    - ((formik.values.currency?.length ?? 0) - currencyCount);
  const amountCount = secondaryDealFiltersHelper.countAmountValues(formik.values);
  const durationCount = secondaryDealFiltersHelper.countDurationValues(formik.values);
  const nominalInterestRateCount = secondaryDealFiltersHelper.countNominalInterestRateValues(formik.values);
  const defaultRateCount = secondaryDealFiltersHelper.countDefaultRateValues(formik.values);
  const discountCount = secondaryDealFiltersHelper.countDiscountValues(formik.values);

  return (
    <Box>
      <Box className={classes.closeButtonContainer}>
        <IconButton onClick={onClose}>
          <RiCloseLine />
        </IconButton>
      </Box>

      <FormikContext.Provider value={formik}>
        <Form className={classes.body}>
          <Box className={classes.title}>
            <Typography variant="subtitle1" fontWeight={500}>
              <FormattedMessage id="all_filters" />
            </Typography>

            {totalFiltersCount > 0 && <InlineBadge label={totalFiltersCount} />}
          </Box>

          <Typography variant="body2" color="text.secondary" marginBottom="32px">
            <FormattedMessage id="select_filters_to_apply" />
          </Typography>

          {withSort && (
            <Accordion className={commonFiltersStyles.accordion} elevation={0}>
              <AccordionSummary expandIcon={<RiArrowDownSFill />}>
                <Typography>
                  <FormattedMessage id="sort_by" />
                </Typography>
              </AccordionSummary>

              <AccordionDetails className={classes.sortContainer}>
                {sortOptions.map((key) => {
                  const value = primaryDealSortVariantsMap[key];

                  return (
                    <Box key={key} className={classes.sortGroup}>
                      <SortButton label={getSortLabel(key)} value={value[SortOrder.ASC]} />

                      <SortButton
                        label={getSortLabel(key)}
                        value={value[SortOrder.DESC]}
                        defaultSelect={key === ESecondaryDealSortOption.PublicationDate}
                      />
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          )}

          <Accordion className={commonFiltersStyles.accordion} elevation={0}>
            <AccordionSummary expandIcon={<RiArrowDownSFill />}>
              <Typography>
                <FormattedMessage id="borrower.type" />
              </Typography>

              {formik.values.productType.length > 0 && (
                <InlineBadge label={formik.values.productType.length} />
              )}
            </AccordionSummary>

            <AccordionDetails>
              <Field
                id="productType"
                name="productType"
                component={InlineSelect}
                options={productTypeOptions}
                multiple
                className={commonFiltersStyles.inlineSelect}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion className={commonFiltersStyles.accordion} elevation={0}>
            <AccordionSummary expandIcon={<RiArrowDownSFill />}>
              <Typography>
                <FormattedMessage id="currency" />
              </Typography>

              {currencyCount > 0 && <InlineBadge label={currencyCount} />}
            </AccordionSummary>

            <AccordionDetails>
              <Field
                id="currency"
                name="currency"
                component={InlineSelect}
                options={currencyFinalOptions}
                multiple
                className={commonFiltersStyles.inlineSelect}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion className={commonFiltersStyles.accordion} elevation={0}>
            <AccordionSummary expandIcon={<RiArrowDownSFill />}>
              <Typography>
                <FormattedMessage id="loan_amount" />
              </Typography>

              {amountCount > 0 && <InlineBadge label={amountCount} />}
            </AccordionSummary>

            <AccordionDetails>
              <Box className={commonFiltersStyles.rangeInput}>
                <Field
                  id="minPrincipal"
                  name="minPrincipal"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'min_amount' })}
                  size="medium"
                />

                <Box className={commonFiltersStyles.dash} />

                <Field
                  id="maxPrincipal"
                  name="maxPrincipal"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'max_amount' })}
                  size="medium"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion className={commonFiltersStyles.accordion} elevation={0}>
            <AccordionSummary expandIcon={<RiArrowDownSFill />}>
              <Typography>
                <FormattedMessage id="loan_duration.in_months" />
              </Typography>

              {durationCount > 0 && <InlineBadge label={durationCount} />}
            </AccordionSummary>

            <AccordionDetails>
              <Box className={commonFiltersStyles.rangeInput}>
                <Field
                  id="minDuration"
                  name="minDuration"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'min_duration' })}
                  size="medium"
                  inputProps={{
                    thousandSeparator: false,
                    decimalScale: 0,
                  }}
                />
                <Box className={commonFiltersStyles.dash} />

                <Field
                  id="maxDuration"
                  name="maxDuration"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'max_duration' })}
                  size="medium"
                  inputProps={{
                    thousandSeparator: false,
                    decimalScale: 0,
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion className={commonFiltersStyles.accordion} elevation={0}>
            <AccordionSummary expandIcon={<RiArrowDownSFill />}>
              <Typography>
                <FormattedMessage id="interest_rate.nominal.short" />
              </Typography>

              {nominalInterestRateCount > 0 && <InlineBadge label={nominalInterestRateCount} />}
            </AccordionSummary>

            <AccordionDetails>
              <Box className={commonFiltersStyles.rangeInput}>
                <Field
                  id="minNominalInterestRate"
                  name="minNominalInterestRate"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'min_amount' })}
                  size="medium"
                  inputProps={{ thousandSeparator: false, decimalScale: 2 }}
                />
                <Box className={commonFiltersStyles.dash} />

                <Field
                  id="maxNominalInterestRate"
                  name="maxNominalInterestRate"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'max_amount' })}
                  size="medium"
                  inputProps={{ thousandSeparator: false, decimalScale: 2 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion className={commonFiltersStyles.accordion} elevation={0}>
            <AccordionSummary expandIcon={<RiArrowDownSFill />}>
              <Typography>
                <FormattedMessage id="expected_default_rate.initial.short" />
              </Typography>

              {defaultRateCount > 0 && <InlineBadge label={defaultRateCount} />}
            </AccordionSummary>

            <AccordionDetails>
              <Box className={commonFiltersStyles.rangeInput}>
                <Field
                  id="minDefaultRate"
                  name="minDefaultRate"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'min_amount' })}
                  size="medium"
                  inputProps={{ thousandSeparator: false, decimalScale: 3 }}
                />
                <Box className={commonFiltersStyles.dash} />

                <Field
                  id="maxDefaultRate"
                  name="maxDefaultRate"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'max_amount' })}
                  size="medium"
                  inputProps={{ thousandSeparator: false, decimalScale: 3 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>


          <Accordion className={commonFiltersStyles.accordion} elevation={0}>
            <AccordionSummary expandIcon={<RiArrowDownSFill />}>
              <Typography>
                <FormattedMessage id="discount_premium" />
              </Typography>

              {discountCount > 0 && <InlineBadge label={discountCount} />}
            </AccordionSummary>

            <AccordionDetails>
              <Box className={commonFiltersStyles.rangeInput}>
                <Field
                  id="minDiscount"
                  name="minDiscount"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'min_amount' })}
                  size="medium"
                  inputProps={{ thousandSeparator: false, decimalScale: 2 }}
                />
                <Box className={commonFiltersStyles.dash} />

                <Field
                  id="maxDiscount"
                  name="maxDiscount"
                  component={AmountTextField}
                  label={intl.formatMessage({ id: 'max_amount' })}
                  size="medium"
                  inputProps={{ thousandSeparator: false, decimalScale: 2 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Rating filter intentionally hidden for markets per product request. */}
        </Form>
      </FormikContext.Provider>

      <Paper className={classes.footer}>
        <Button variant="text" onClick={handleClear}>
          <FormattedMessage id="filters.clear" />
        </Button>

        <Button onClick={() => handleSubmit(formik.values)}>
          <FormattedMessage id="filters.apply" />
        </Button>
      </Paper>
    </Box>
  );
});
